import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Plus, Trash2, Users2 } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import {
  attendeeFieldsSetupQueryOptions,
  RULE_OPS,
  upsertAttendeeGroup,
  type AttendeeField,
  type AttendeeGroup,
  type RuleClause,
  type RuleOp,
  type UpsertGroupInput,
  type Visibility,
} from "@/core/attendee-fields"
import { ApiError } from "@/core/api"
import { ticketsQueryOptions, type TicketType } from "@/core/payments"
import { ResponsiveSheet } from "@/components/ui/ResponsiveSheet"
import {
  Card,
  Field,
  GhostButton,
  Pill,
  PrimaryButton,
  Select,
  SettingsPage,
  Textarea,
  TextInput,
  Toggle,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/attendee-groups")({
  component: AttendeeGroupsView,
})

function AttendeeGroupsView() {
  const { eventId } = useActiveEvent()
  const q = useQuery({
    ...attendeeFieldsSetupQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const groups = q.data?.groups ?? []
  const fields = q.data?.fields ?? []

  const [editing, setEditing] = useState<AttendeeGroup | "new" | null>(null)

  return (
    <SettingsPage
      title="Attendee groups"
      description="Cohorts you can target with channels, todos and documents — manual or rule-driven."
      actions={
        <PrimaryButton onClick={() => setEditing("new")} disabled={!eventId}>
          <Plus className="h-4 w-4" />
          New group
        </PrimaryButton>
      }
    >
      {q.isError && (
        <Card>
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {(q.error as Error)?.message ?? "Failed to load groups"}
          </p>
        </Card>
      )}

      {groups.length === 0 && !q.isLoading ? (
        <Card>
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-plum-100 text-plum-700/60 dark:bg-white/10 dark:text-plum-200/50">
              <Users2 className="h-5 w-5" />
            </span>
            <p className="text-[13px] font-semibold text-plum-900 dark:text-plum-50">
              No groups yet
            </p>
            <p className="max-w-xs text-[12.5px] text-plum-700/55 dark:text-plum-200/40">
              Automatic groups update themselves as attendee fields change.
            </p>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="divide-y divide-plum-200/50 dark:divide-white/5">
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setEditing(g)}
                className="flex w-full items-center gap-3 py-2.5 text-left transition hover:bg-plum-50/60 dark:hover:bg-white/[0.03]"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                      {g.name}
                    </span>
                    {g.isAutomatic && <Pill tone="accent">Automatic</Pill>}
                  </div>
                  <p className="mt-0.5 text-[11.5px] text-plum-700/50 dark:text-plum-200/35">
                    {g.memberCount} member{g.memberCount === 1 ? "" : "s"}
                    {g.description ? ` · ${g.description}` : ""}
                  </p>
                </div>
                {g.visibility !== "admin_only" && <Pill>Visible</Pill>}
              </button>
            ))}
          </div>
        </Card>
      )}

      {editing && eventId && (
        <GroupSheet
          eventId={eventId}
          group={editing === "new" ? null : editing}
          fields={fields}
          onClose={() => setEditing(null)}
        />
      )}
    </SettingsPage>
  )
}

const STANDARD_FIELD_KEYS: { key: string; label: string }[] = [
  { key: "company", label: "Company" },
  { key: "title", label: "Job title" },
  { key: "country", label: "Country" },
]

// New ticket-driven cohorts should match the stable ticket type id. Keep the
// legacy readable-name key visible for existing groups so renames do not silently
// rewrite old rules.
const TICKET_TYPE_ID_FIELD_KEY = "guest.ticket_type_id"
const LEGACY_TICKET_TYPE_FIELD_KEY = "guest.ticket_type"
type RuleBlock = { clauses: RuleClause[] }

function isTicketTypeField(fieldKey: string) {
  return fieldKey === TICKET_TYPE_ID_FIELD_KEY || fieldKey === LEGACY_TICKET_TYPE_FIELD_KEY
}

function ticketRuleValue(ticket: TicketType, fieldKey: string) {
  return fieldKey === LEGACY_TICKET_TYPE_FIELD_KEY ? ticket.name : ticket.id
}

function clauseTextValue(clause: RuleClause) {
  return clause.value ?? clause.values?.join(", ") ?? ""
}

function emptyClause(): RuleClause {
  return { fieldKey: "", op: "equals", value: "" }
}

function isAllBlock(entry: RuleClause | { all: RuleClause[] }): entry is { all: RuleClause[] } {
  return Array.isArray((entry as { all?: unknown }).all)
}

function initialRuleBlocks(group: AttendeeGroup | null): RuleBlock[] {
  const rule = group?.rule
  if (!rule) return [{ clauses: [emptyClause()] }]
  if (rule.any && rule.any.length > 0) {
    return rule.any.map((entry) => {
      if (isAllBlock(entry)) {
        return { clauses: entry.all.length > 0 ? entry.all : [emptyClause()] }
      }
      return { clauses: [entry] }
    })
  }
  return [{ clauses: rule.all && rule.all.length > 0 ? rule.all : [emptyClause()] }]
}

function GroupSheet({
  eventId,
  group,
  fields,
  onClose,
}: {
  eventId: string
  group: AttendeeGroup | null
  fields: AttendeeField[]
  onClose: () => void
}) {
  const qc = useQueryClient()
  const isEdit = !!group

  const [name, setName] = useState(group?.name ?? "")
  const [description, setDescription] = useState(group?.description ?? "")
  // Visibility is a single "shown to attendees or not" toggle: members_only and
  // directory behave identically today, so we collapse the picker but preserve
  // whichever shown-level a group already has.
  const [visibleToAttendees, setVisibleToAttendees] = useState(group?.visibility !== "admin_only")
  const visibility: Visibility = visibleToAttendees
    ? group?.visibility && group.visibility !== "admin_only"
      ? group.visibility
      : "members_only"
    : "admin_only"
  const [isAutomatic, setIsAutomatic] = useState(group?.isAutomatic ?? false)
  const [ruleBlocks, setRuleBlocks] = useState<RuleBlock[]>(() => initialRuleBlocks(group))

  // Ticket types drive an automatic-cohort field ("guest.ticket_type_id"). Only
  // offered when the event actually has ticket types defined.
  const ticketTypesQ = useQuery({
    ...ticketsQueryOptions(eventId),
    enabled: !!eventId,
  })
  const ticketTypes = ticketTypesQ.data ?? []

  const fieldOptions = [
    ...STANDARD_FIELD_KEYS,
    ...(ticketTypes.length > 0
      ? [{ key: TICKET_TYPE_ID_FIELD_KEY, label: "Ticket type" }]
      : []),
    ...(ruleBlocks.some((block) =>
      block.clauses.some((c) => c.fieldKey === LEGACY_TICKET_TYPE_FIELD_KEY),
    )
      ? [{ key: LEGACY_TICKET_TYPE_FIELD_KEY, label: "Ticket type name" }]
      : []),
    ...fields.map((f) => ({ key: f.key, label: f.label })),
  ]

  const save = useMutation({
    mutationFn: () => {
      if (!name.trim()) throw new ApiError("Name is required", 400)
      const input: UpsertGroupInput = {
        ...(group ? { id: group.id } : {}),
        name: name.trim(),
        description: description.trim() || undefined,
        visibility,
        isAutomatic,
      }
      if (isAutomatic) {
        const validBlocks = ruleBlocks
          .map((block) => block.clauses.filter((c) => c.fieldKey))
          .filter((block) => block.length > 0)
        if (validBlocks.length === 0)
          throw new ApiError("Automatic groups need at least one rule", 400)
        const serializeClause = (c: RuleClause): RuleClause => {
            const meta = RULE_OPS.find((o) => o.value === c.op)
            const out: RuleClause = { fieldKey: c.fieldKey, op: c.op }
            if (meta?.needsValue) {
              if (meta.multi) {
                out.values = c.values?.length
                  ? c.values
                  : (c.value ?? "")
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
              } else {
                out.value = c.value ?? ""
              }
            }
            return out
          }
        const serializedBlocks = validBlocks.map((block) => block.map(serializeClause))
        input.rule =
          serializedBlocks.length === 1
            ? { all: serializedBlocks[0] }
            : { any: serializedBlocks.map((all) => ({ all })) }
      }
      return upsertAttendeeGroup(eventId, input)
    },
    onSuccess: () => {
      toast.success(isEdit ? "Group updated" : "Group created")
      void qc.invalidateQueries({
        queryKey: ["resource", "attendee_field_definitions", { eventId, mode: "setup" }],
      })
      onClose()
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Failed to save group"),
  })

  const totalClauseCount = ruleBlocks.reduce((sum, block) => sum + block.clauses.length, 0)

  const setClause = (blockIndex: number, clauseIndex: number, patch: Partial<RuleClause>) =>
    setRuleBlocks((blocks) =>
      blocks.map((block, bIdx) =>
        bIdx === blockIndex
          ? {
              clauses: block.clauses.map((clause, cIdx) =>
                cIdx === clauseIndex ? { ...clause, ...patch } : clause,
              ),
            }
          : block,
      ),
    )

  const addClause = (blockIndex: number) =>
    setRuleBlocks((blocks) =>
      blocks.map((block, bIdx) =>
        bIdx === blockIndex ? { clauses: [...block.clauses, emptyClause()] } : block,
      ),
    )

  const removeClause = (blockIndex: number, clauseIndex: number) =>
    setRuleBlocks((blocks) => {
      const next = blocks
        .map((block, bIdx) =>
          bIdx === blockIndex
            ? { clauses: block.clauses.filter((_, cIdx) => cIdx !== clauseIndex) }
            : block,
        )
        .filter((block) => block.clauses.length > 0)
      return next.length > 0 ? next : [{ clauses: [emptyClause()] }]
    })

  const insertOrBlock = (afterIndex: number) =>
    setRuleBlocks((blocks) => [
      ...blocks.slice(0, afterIndex + 1),
      { clauses: [emptyClause()] },
      ...blocks.slice(afterIndex + 1),
    ])

  const removeBlock = (blockIndex: number) =>
    setRuleBlocks((blocks) => {
      const next = blocks.filter((_, idx) => idx !== blockIndex)
      return next.length > 0 ? next : [{ clauses: [emptyClause()] }]
    })

  return (
    <ResponsiveSheet
      open
      onClose={onClose}
      title={isEdit ? "Edit group" : "New attendee group"}
      subtitle={isEdit ? `${group?.memberCount} members` : "Define a cohort"}
      icon={<Users2 className="h-4 w-4" />}
      width={520}
      footer={
        <div className="flex justify-end gap-2">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : isEdit ? "Save group" : "Create group"}
          </PrimaryButton>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="VIP guests"
            autoFocus
          />
        </Field>
        <Field label="Description">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional — shown in the directory."
            rows={2}
          />
        </Field>
        <Toggle
          label="Visible to attendees"
          description="Members can see this group and its fellow members in their app. Off keeps it organizer-only."
          checked={visibleToAttendees}
          onChange={setVisibleToAttendees}
        />

        <Toggle
          label="Automatic membership"
          description="Members are derived from attendee fields and stay in sync."
          checked={isAutomatic}
          onChange={setIsAutomatic}
        />

        {isAutomatic && (
          <div className="space-y-3 rounded-xl border border-plum-200/60 p-3 dark:border-white/10">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11.5px] font-semibold uppercase tracking-wide text-plum-700/45 dark:text-plum-200/30">
                Match
              </p>
            </div>
            {ruleBlocks.map((block, blockIndex) => (
              <div key={blockIndex} className="space-y-2.5">
                <div className="rounded-xl border border-plum-200/60 p-3 dark:border-white/10">
                  <div className="mb-2.5 flex items-center justify-between gap-2">
                    <span className="rounded-md bg-plum-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-plum-800 dark:bg-white/10 dark:text-plum-100">
                      All
                    </span>
                    {ruleBlocks.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBlock(blockIndex)}
                        className="grid h-7 w-7 place-items-center rounded-lg text-rose-500/80 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10"
                        title="Remove rule block"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {block.clauses.map((c, clauseIndex) => {
                      const meta = RULE_OPS.find((o) => o.value === c.op)
                      return (
                        <div key={clauseIndex} className="flex flex-wrap items-center gap-2">
                          <Select
                            className="h-8 flex-1"
                            value={c.fieldKey}
                            onChange={(e) =>
                              setClause(blockIndex, clauseIndex, {
                                fieldKey: e.target.value,
                                value: "",
                                values: [],
                              })
                            }
                          >
                            <option value="">Field…</option>
                            {fieldOptions.map((f) => (
                              <option key={f.key} value={f.key}>
                                {f.label}
                              </option>
                            ))}
                          </Select>
                          <Select
                            className="h-8 w-[112px]"
                            value={c.op}
                            onChange={(e) =>
                              setClause(blockIndex, clauseIndex, {
                                op: e.target.value as RuleOp,
                                value: "",
                                values: [],
                              })
                            }
                          >
                            {RULE_OPS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Select>
                          {meta?.needsValue &&
                            (isTicketTypeField(c.fieldKey) ? (
                              meta.multi ? (
                                <Select
                                  multiple
                                  className="min-h-[74px] flex-1 py-1"
                                  value={c.values ?? []}
                                  size={Math.min(4, Math.max(2, ticketTypes.length))}
                                  onChange={(e) =>
                                    setClause(blockIndex, clauseIndex, {
                                      values: Array.from(e.currentTarget.selectedOptions).map(
                                        (option) => option.value,
                                      ),
                                      value: "",
                                    })
                                  }
                                >
                                  {ticketTypes.map((t) => (
                                    <option key={t.id} value={ticketRuleValue(t, c.fieldKey)}>
                                      {t.name}
                                    </option>
                                  ))}
                                </Select>
                              ) : (
                                <Select
                                  className="h-8 flex-1"
                                  value={c.value ?? ""}
                                  onChange={(e) =>
                                    setClause(blockIndex, clauseIndex, {
                                      value: e.target.value,
                                      values: [],
                                    })
                                  }
                                >
                                  <option value="">Ticket type…</option>
                                  {ticketTypes.map((t) => (
                                    <option key={t.id} value={ticketRuleValue(t, c.fieldKey)}>
                                      {t.name}
                                    </option>
                                  ))}
                                </Select>
                              )
                            ) : (
                              <TextInput
                                className="h-8 flex-1"
                                placeholder={meta.multi ? "a, b, c" : "value"}
                                value={clauseTextValue(c)}
                                onChange={(e) =>
                                  setClause(blockIndex, clauseIndex, {
                                    value: e.target.value,
                                    values: [],
                                  })
                                }
                              />
                            ))}
                          {totalClauseCount > 1 && (
                            <button
                              type="button"
                              onClick={() => removeClause(blockIndex, clauseIndex)}
                              className="grid h-8 w-8 place-items-center rounded-lg text-rose-500/80 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10"
                              title="Remove condition"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <GhostButton className="mt-2 h-8" onClick={() => addClause(blockIndex)}>
                    <Plus className="h-3.5 w-3.5" />
                    Add condition
                  </GhostButton>
                </div>
                <div className="flex justify-center">
                  <GhostButton className="h-8" onClick={() => insertOrBlock(blockIndex)}>
                    <Plus className="h-3.5 w-3.5" />
                    OR
                  </GhostButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ResponsiveSheet>
  )
}
